import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { FiEdit2, FiTrash2, FiX, FiCamera, FiThermometer, FiCloudRain } from "react-icons/fi";
import { GiPlantRoots } from "react-icons/gi";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import QRScanner from "../components/QRScanner";
import { getMCUsByClientId } from "../api/mcuApi";
import {
  scanSensorsByMAC,
  createSensor,
  updateSensor,
  deleteSensor,
} from "../api/sensorApi";
import SensorFormModal from "../components/SensorModalForm";

const SensorManager = () => {
  const { clientData } = useAuth();
  const [mcus, setMcus] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editingSensor, setEditingSensor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All");

  const scannerRef = useRef();
  const { register, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm();

  const sensorTypes = ["CO2", "Humidity", "Soil_temp", "Moisture"];
  const sensorIconMap = {
    CO2: FiCloudRain,
    Humidity: FiThermometer,
    Soil_temp: GiPlantRoots,
    Moisture: FiCloudRain,
  };

  useEffect(() => {
    loadSensorData();
  }, [clientData]);

  const loadSensorData = async () => {
    if (!clientData?.client_id) return;
    setLoading(true);
    try {
      const mcusResult = await getMCUsByClientId(clientData.client_id);
      const flatSensors = mcusResult.flatMap(mcu =>
        (mcu.sensors || []).map(s => ({
          ...s,
          mcu_id: mcu.mcu_id,
          mcu_name: mcu.name,
        }))
      );
      setMcus(mcusResult);
      setSensors(flatSensors);
    } catch (err) {
      toast.error(err.message || "Failed to load sensors");
    } finally {
      setLoading(false);
    }
  };

const openModal = (sensor = null) => {
  setEditingSensor(sensor);
  if (sensor) {
    reset({
      mac: sensor.mac,
      name: sensor.name,
      type: sensor.type,
      mcu_id: sensor.mcu_id,
    });
  } else {
    reset({ mac: "", name: "", type: "", mcu_id: "" });
  }
  setShowModal(true);
};
  const closeModal = () => setShowModal(false);

  const openScanner = () => setShowScanner(true);
  const forceCloseScanner = () => {
    scannerRef.current?.stopCamera?.();
    setShowScanner(false);
  };

const handleScan = async (mac) => {
  setShowScanner(false);
  setModalLoading(true);
  try {
    const sensor = await scanSensorsByMAC(mac);
    if(!sensor) { toast.success(`Sensor ${mac} Not Found, Or Already Used`); return;}
    setEditingSensor(sensor);
    reset({
      mac: sensor.mac,
      name: sensor.name,
      type: sensor.type,
      mcu_id: sensor.mcu_id,
    });
    setShowModal(true);
    toast.success(`Loaded sensor ${mac}`);
  } catch {
    setEditingSensor({ mac });
    reset({ mac, name: "", type: "", mcu_id: "" });
    setShowModal(true);
    toast.info(`No sensor found for MAC: ${mac}`);
  } finally {
    setModalLoading(false);
  }
};

  const handleDelete = async (sensor_id) => {
    if (!window.confirm("Delete this sensor?")) return;
    setDeleteLoading(sensor_id);
    try {
      await deleteSensor(sensor_id);
      toast.success("Sensor deleted");
      setSensors(s => s.filter(x => x.sensor_id !== sensor_id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteLoading(null);
    }
  };

  const onSubmit = async formData => {
    setModalLoading(true);
    try {
      if (editingSensor?.sensor_id) {
        await updateSensor(editingSensor.sensor_id, formData);
        toast.success("Sensor updated");
      } else {
        await createSensor(formData);
        toast.success("Sensor created");
      }
      closeModal();
      loadSensorData();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setModalLoading(false);
    }
  };

  const filtered = sensors.filter(
    (s) =>
      (filterType === "All" || s.type === filterType) &&
      (s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.mac?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Sensors</h1>
        <button onClick={openScanner} className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          <FiCamera className="mr-2" /> Scan Sensor
        </button>
      </div>

      <div className="flex gap-4">
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or MAC..."
          className="flex-1 px-4 py-2 border rounded-lg"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option>All</option>
          {sensorTypes.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p>Loading sensors…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((s) => {
            const Icon = sensorIconMap[s.type] || FiThermometer;
            return (
              <div key={s.sensor_id} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon className="text-xl text-blue-600" />
                    <div>
                      <h3 className="font-semibold">{s.name || "Unnamed"}</h3>
                      <p className="text-sm text-gray-500">{s.mac}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openModal(s)} className="text-blue-600 hover:text-blue-800"><FiEdit2 /></button>
                    <button onClick={() => handleDelete(s.sensor_id)} disabled={deleteLoading === s.sensor_id} className="text-red-600 hover:text-red-800">
                      {deleteLoading === s.sensor_id ? "Deleting…" : <FiTrash2 />}
                    </button>
                  </div>
                </div>
                <p className="text-sm"><strong>Type:</strong> {s.type}</p>
                <p className="text-sm"><strong>MCU:</strong> {s.mcu_name}</p>
              </div>
            );
          })}
        </div>
      )}

      {showScanner && (
        <QRScanner
          ref={scannerRef}
          isOpen={showScanner}
          onClose={forceCloseScanner}
          onScan={handleScan}
          onError={() => toast.error("Scanner error")}
          title="Scan Sensor QR"
          instructions="Align QR/barcode inside the frame"
        />
      )}

 <SensorFormModal
  sensor={editingSensor}
  mcus={mcus}
  isOpen={showModal}
  onClose={closeModal}
  onSubmit={onSubmit}
  loading={modalLoading}
/>
    </div>
  );
};

export default SensorManager;
